import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, Tag, Phone, Save, PoundSterling, MessageSquare, AlertTriangle } from "lucide-react";
import { toast } from "../../hooks/use-toast";

function newPreset() {
  return { id: `p_${Math.random().toString(36).slice(2, 9)}`, label: "", price: "" };
}

export default function SettingsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presets, setPresets] = useState([]);
  const [contactPhone, setContactPhone] = useState("");
  const [passFees, setPassFees] = useState(false);
  const [refundAlertPct, setRefundAlertPct] = useState("15");
  const [monthlyStatement, setMonthlyStatement] = useState(true);
  const [archiveDays, setArchiveDays] = useState("30");
  const [suggestSameNight, setSuggestSameNight] = useState(true);
  const [rate, setRate] = useState({ pct: 0, fixed: 0 });
  const [example, setExample] = useState("10");
  const [sms, setSms] = useState(null);
  const [smsBusy, setSmsBusy] = useState(false);

  useEffect(() => {
    api.get("/business/me").then(({ data }) => {
      setPresets((data.door_price_presets || []).map((p) => ({ ...p, price: p.price })));
      setContactPhone(data.contact_phone || "");
      setPassFees(!!data.pass_fees_to_customer);
      setRefundAlertPct(data.refund_alert_pct != null ? String(data.refund_alert_pct) : "15");
      setMonthlyStatement(data.monthly_statement_email !== false);
      setArchiveDays(data.auto_archive_days != null ? String(data.auto_archive_days) : "30");
      setSuggestSameNight(data.suggest_same_night !== false);
      setLoading(false);
    });
    api.get("/business/payout").then(({ data }) => setRate({ pct: data.commission_pct || 0, fixed: data.commission_fixed || 0 })).catch(() => {});
    api.get("/business/sms-confirmation").then(({ data }) => setSms(data)).catch(() => {});
  }, []);

  const toggleSms = async () => {
    const next = !sms.enabled;
    if (next && !window.confirm(`Turn on booking-confirmation texts? This will increase your monthly fee to £${sms.new_monthly_fee.toFixed(2)}.`)) return;
    setSmsBusy(true);
    try {
      const { data } = await api.post("/business/sms-confirmation", { enabled: next });
      setSms((s) => ({ ...s, enabled: data.enabled, new_monthly_fee: data.new_monthly_fee }));
      toast({ title: next ? "Booking-confirmation SMS enabled" : "Booking-confirmation SMS disabled", description: next ? `Your monthly fee is now £${data.new_monthly_fee.toFixed(2)}.` : "Your monthly fee reverts to the standard rate." });
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Could not update", variant: "destructive" });
    } finally { setSmsBusy(false); }
  };

  const addPreset = () => setPresets([...presets, newPreset()]);
  const rmPreset = (id) => setPresets(presets.filter((p) => p.id !== id));
  const setPreset = (id, k, v) => setPresets(presets.map((p) => (p.id === id ? { ...p, [k]: v } : p)));

  const save = async () => {
    const clean = presets
      .filter((p) => p.label.trim() && p.price !== "" && !isNaN(parseFloat(p.price)))
      .map((p) => ({ id: p.id, label: p.label.trim(), price: Math.max(0, parseFloat(p.price)) }));
    setSaving(true);
    try {
      await api.put("/business/me", { door_price_presets: clean, contact_phone: contactPhone.trim(), pass_fees_to_customer: passFees, refund_alert_pct: Math.max(0, Math.min(100, parseFloat(refundAlertPct) || 0)), monthly_statement_email: monthlyStatement, auto_archive_days: parseInt(archiveDays) || 0, suggest_same_night: suggestSameNight });
      setPresets(clean.map((p) => ({ ...p })));
      toast({ title: "Settings saved", description: `${clean.length} door price preset(s) saved.` });
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not save settings", variant: "destructive" });
    } finally { setSaving(false); }
  };

  if (loading) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  const exPrice = Math.max(0, parseFloat(example) || 0);
  const exFee = Math.round((exPrice * (rate.pct || 0) / 100 + (rate.fixed || 0)) * 100) / 100;
  const customerPays = passFees ? exPrice + exFee : exPrice;
  const youKeep = passFees ? exPrice : Math.max(0, exPrice - exFee);

  return (
    <div className="max-w-[640px] space-y-6" data-testid="settings-tab">
      {/* Door price presets */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex items-center gap-2 mb-1"><Tag className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Door price presets</h3></div>
        <p className="text-[#5a6b80] text-sm mb-4">Save common one-off door prices (e.g. "Student £5", "On the door £10"). They appear as one-tap buttons in the Box office when you sell a paid ticket in person.</p>
        <div className="space-y-2 mb-3">
          {presets.length === 0 && <p className="text-sm text-[#8a97a8]">No presets yet — add your first below.</p>}
          {presets.map((p, i) => (
            <div key={p.id} className="flex gap-2 items-center" data-testid={`preset-row-${i}`}>
              <input placeholder="Label (e.g. Student)" value={p.label} onChange={(e) => setPreset(p.id, "label", e.target.value)} data-testid={`preset-label-${i}`}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a97a8] text-sm">£</span>
                <input type="number" step="0.01" min="0" placeholder="0.00" value={p.price} onChange={(e) => setPreset(p.id, "price", e.target.value)} data-testid={`preset-price-${i}`}
                  className="w-full rounded-lg border border-gray-200 pl-6 pr-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              </div>
              <button type="button" onClick={() => rmPreset(p.id)} data-testid={`preset-remove-${i}`} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addPreset} data-testid="preset-add" className="text-[#1fb0b0] text-sm font-medium flex items-center gap-1"><Plus size={14} /> Add preset</button>
      </div>

      {/* Billing contact */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex items-center gap-2 mb-1"><Phone className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Billing contact mobile</h3></div>
        <p className="text-[#5a6b80] text-sm mb-4">If you're billed the monthly platform fee, we'll text this number if an invoice becomes overdue (in addition to emailing you). Include the country code, e.g. +44…</p>
        <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="contact-phone-input"
          placeholder="+44 7700 900000" className="w-full sm:w-72 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
      </div>

      {/* Auto monthly statement */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="monthly-statement-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1"><Save className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Automatic monthly statement</h3></div>
            <p className="text-[#5a6b80] text-sm">On the 1st of each month we'll email you a PDF payout statement for the previous month — perfect for your accountant.</p>
          </div>
          <button type="button" role="switch" aria-checked={monthlyStatement} data-testid="monthly-statement-toggle"
            onClick={() => setMonthlyStatement((v) => !v)}
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${monthlyStatement ? "bg-[#2ec9c9]" : "bg-gray-300"}`}>
            <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${monthlyStatement ? "translate-x-5" : ""}`} />
          </button>
        </div>
      </div>

      {/* Auto-archive window */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="archive-window-card">
        <div className="flex items-center gap-2 mb-1"><Save className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Auto-archive past events</h3></div>
        <p className="text-[#5a6b80] text-sm mb-4">Automatically archive events this long after they finish so your live Events list stays tidy. Archived events show a 404 to customers but keep all their data and can be restored anytime from the Past &amp; closed tab.</p>
        <select value={archiveDays} onChange={(e) => setArchiveDays(e.target.value)} data-testid="archive-window-select"
          className="w-full sm:w-64 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] text-sm bg-white">
          <option value="7">7 days after the event</option>
          <option value="30">30 days after the event</option>
          <option value="90">90 days after the event</option>
          <option value="0">Never (I'll archive manually)</option>
        </select>
        <label className="flex items-center gap-2.5 mt-5 pt-5 border-t border-gray-100 cursor-pointer" data-testid="same-night-toggle">
          <input type="checkbox" checked={suggestSameNight} onChange={(e) => setSuggestSameNight(e.target.checked)} data-testid="same-night-checkbox" className="w-4 h-4 accent-[#2ec9c9]" />
          <span className="text-sm text-[#16243b]">Suggest my other shows on the same night after checkout
            <span className="block text-xs text-[#8a97a8] mt-0.5">On the ticket page, show buyers your other shows on the same date that start at a different time (cross-sell tracked in Promo results).</span></span>
        </label>
      </div>
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="refund-alert-card">
        <div className="flex items-center gap-2 mb-1"><AlertTriangle className="text-[#f47c7c]" size={20} /><h3 className="font-semibold text-[#16243b]">Refund alerts</h3></div>
        <p className="text-[#5a6b80] text-sm mb-4">We'll flag any event on your Overview whose refund rate climbs above this percentage, so you can spot problem shows early.</p>
        <div className="flex items-center gap-2">
          <input type="number" min="0" max="100" step="1" value={refundAlertPct} onChange={(e) => setRefundAlertPct(e.target.value)} data-testid="refund-alert-input"
            className="w-24 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
          <span className="text-[#5a6b80] text-sm">% refund rate</span>
        </div>
      </div>

      {/* Booking fees */}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex items-center gap-2 mb-1"><PoundSterling className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Booking fees</h3></div>
        <p className="text-[#5a6b80] text-sm mb-4">By default the platform fee is deducted from your ticket revenue. Turn this on to add the fee on top of the ticket price at checkout, so the customer pays it and you keep the full face value.</p>
        <button type="button" onClick={() => setPassFees((v) => !v)} data-testid="pass-fees-toggle"
          className={`w-full flex items-center justify-between rounded-xl border-2 px-4 py-3 transition-colors ${passFees ? "border-[#2ec9c9] bg-[#eefafa]" : "border-gray-200"}`}>
          <div className="text-left">
            <p className="font-medium text-[#16243b]">Pass booking fees to the customer</p>
            <p className="text-xs text-[#8a97a8]">{passFees ? "Customer pays ticket price + booking fee." : "You absorb the fee (customer pays face value)."}</p>
          </div>
          <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${passFees ? "bg-[#2ec9c9] justify-end" : "bg-gray-300 justify-start"}`}>
            <span className="w-5 h-5 bg-white rounded-full shadow" />
          </span>
        </button>
        <div className="mt-4 rounded-xl bg-[#f6f8fb] p-4" data-testid="fee-preview">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-[#16243b]">Live preview</p>
            <div className="flex items-center gap-1.5 text-sm">
              <span className="text-[#8a97a8]">Ticket price £</span>
              <input type="number" step="0.01" min="0" value={example} onChange={(e) => setExample(e.target.value)} data-testid="fee-preview-price"
                className="w-20 rounded-lg border border-gray-200 px-2 py-1 outline-none focus:border-[#2ec9c9]" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-white p-3 text-center">
              <p className="text-xs text-[#8a97a8]">Customer pays</p>
              <p className="text-xl font-bold text-[#16243b]" data-testid="fee-preview-customer">£{customerPays.toFixed(2)}</p>
            </div>
            <div className="rounded-lg bg-white p-3 text-center">
              <p className="text-xs text-[#8a97a8]">You keep</p>
              <p className="text-xl font-bold text-[#2ec9c9]" data-testid="fee-preview-keep">£{youKeep.toFixed(2)}</p>
            </div>
          </div>
          <p className="text-xs text-[#8a97a8] mt-2 text-center">Platform fee £{exFee.toFixed(2)} ({rate.pct}%{rate.fixed ? ` + £${rate.fixed}/ticket` : ""}) — {passFees ? "added on top for the customer." : "deducted from your revenue."}</p>
        </div>
      </div>

      {sms && sms.feature_on && (
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="sms-confirmation-card">
          <div className="flex items-center gap-2 mb-1"><MessageSquare className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Booking-confirmation texts</h3></div>
          <p className="text-[#5a6b80] text-sm mb-4">Send buyers an instant "you're booked" text message the moment they purchase, with a link to their tickets. This is a paid add-on — turning it on changes your monthly fee to <strong>£{sms.new_monthly_fee.toFixed(2)}</strong> (from £{sms.base_fee.toFixed(2)}).</p>
          <button type="button" onClick={toggleSms} disabled={smsBusy} data-testid="sms-confirmation-toggle"
            className={`w-full flex items-center justify-between rounded-xl border-2 px-4 py-3 transition-colors disabled:opacity-60 ${sms.enabled ? "border-[#2ec9c9] bg-[#eefafa]" : "border-gray-200"}`}>
            <div className="text-left">
              <p className="font-medium text-[#16243b]">Instant booking-confirmation SMS</p>
              <p className="text-xs text-[#8a97a8]">{sms.enabled ? `On — £${sms.new_monthly_fee.toFixed(2)}/mo. Buyers with a mobile get an instant text.` : `Off — standard £${sms.base_fee.toFixed(2)}/mo.`}</p>
            </div>
            <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${sms.enabled ? "bg-[#2ec9c9] justify-end" : "bg-gray-300 justify-start"}`}>
              <span className="w-5 h-5 bg-white rounded-full shadow" />
            </span>
          </button>
        </div>
      )}

      <button disabled={saving} onClick={save} data-testid="settings-save-btn" className="gp-btn-teal rounded-xl px-6 py-3 font-medium flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Save settings
      </button>
    </div>
  );
}
